"use client";

import Script from "next/script";
import React, { useEffect, useMemo, useState } from "react";
import { log } from "@/lib/utils/logger";
import { getRecaptchaSiteKey } from "@/lib/utils/recaptcha";
import { useRecaptchaScript, updateRecaptchaScriptState } from "@/hooks/useRecaptchaScript";

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'Unknown error';
};

/**
 * RecaptchaLoader component that safely loads the reCAPTCHA script
 * with proper environment variable validation and memoization to prevent multiple loads
 */
export const RecaptchaLoader: React.FC = () => {
  const { isLoaded, isLoading } = useRecaptchaScript();
  const [siteKey, setSiteKey] = useState<string | undefined | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadSiteKey = async () => {
      try {
        const resolvedSiteKey = await getRecaptchaSiteKey();
        if (isMounted) {
          setSiteKey(resolvedSiteKey);
        }
      } catch (error) {
        log.error('Failed to resolve reCAPTCHA site key', getErrorMessage(error));
        if (isMounted) {
          setSiteKey(undefined);
        }
      }
    };

    void loadSiteKey();

    return () => {
      isMounted = false;
    };
  }, []);

  // Memoize the site key validation and URL generation
  const { isValid, recaptchaUrl } = useMemo(() => {
    if (siteKey === null) {
      return { isValid: false, recaptchaUrl: null };
    }

    // Validate that the site key is available (check for undefined/null specifically)
    if (siteKey === undefined || siteKey === null) {
      const error = 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY environment variable is not set. reCAPTCHA will not be loaded.';
      log.warn(error);
      return { isValid: false, recaptchaUrl: null };
    }

    // Validate that the site key is not just whitespace or empty
    if (siteKey.trim() === '') {
      const error = 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY environment variable is empty. reCAPTCHA will not be loaded.';
      log.warn(error);
      return { isValid: false, recaptchaUrl: null };
    }

    const url = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}&onload=onRecaptchaLoad`;
    return { isValid: true, recaptchaUrl: url };
  }, [siteKey]);

  // Memoized script load handlers
  const handleScriptLoad = useMemo(() => {
    return () => {
      log.debug('reCAPTCHA script loaded successfully');
      updateRecaptchaScriptState.setLoaded();
    };
  }, []);

  const handleScriptError = useMemo(() => {
    return (error: Event | string) => {
      const errorMsg = getErrorMessage(error);
      log.error('Failed to load reCAPTCHA script', errorMsg);
      updateRecaptchaScriptState.setError(errorMsg);
    };
  }, []);

  // Don't render if invalid configuration
  if (!isValid) {
    return null;
  }

  // Don't render if script is already loaded
  if (isLoaded) {
    log.debug('reCAPTCHA script already loaded, skipping loader');
    return null;
  }

  // Don't render multiple instances if already loading
  if (isLoading) {
    log.debug('reCAPTCHA script is already loading, skipping duplicate');
    return null;
  }

  // Mark as loading when we're about to render the script
  updateRecaptchaScriptState.setLoading();

  return (
    <Script
      src={recaptchaUrl!} // We know this is valid since isValid is true
      strategy="afterInteractive"
      onLoad={handleScriptLoad}
      onError={handleScriptError}
    />
  );
};

export default RecaptchaLoader;
